import { entityApi } from '@/features/Entity/api/entityApi';

export function classOfSharesToShareType(classCd: string, classLabel?: string): string | null {
  const cd = String(classCd || '').trim();
  if (cd === 'A20001') return 'Ordinary';
  if (cd === 'A20002') return 'Preference';
  if (cd === 'A20003') return 'Others';
  const name = String(classLabel || '').toLowerCase();
  if (name.includes('ordinary')) return 'Ordinary';
  if (name.includes('preference') || name.includes('pref')) return 'Preference';
  if (name.includes('other')) return 'Others';
  return null;
}

export function parseShareCapitalAmount(value: unknown): number {
  const n = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

export function formatShareCapitalAmount(value: number): string {
  if (!Number.isFinite(value)) return '';
  if (value === 0) return '0';
  return String(value);
}

export function computeIssuedFromBaseline(baseline: number, paidUp: unknown): string {
  return formatShareCapitalAmount(baseline + parseShareCapitalAmount(paidUp));
}

/**
 * Entity Shares list — dedupe by shareCapitalIdx, sum issued for a currency (all share types).
 * SG Allotment preset treats one class per currency, so the company issued baseline is
 * aggregated per currency regardless of class.
 */
export function sumEntityIssuedBaselineByCurrency(
  shares: Array<Record<string, unknown>>,
  currencyCd: string,
): number {
  const currency = String(currencyCd || '').trim();
  if (!currency) return 0;

  const seen = new Set<string>();
  let sum = 0;
  for (const raw of shares) {
    const rowCurrency = String(raw.currencyCode ?? raw.CURRENCY_CD ?? '').trim();
    if (rowCurrency !== currency) continue;

    const idx = raw.shareCapitalIdx ?? raw.SHARE_CAPITAL_IDX;
    const rowType = String(raw.shareType ?? raw.share_type ?? '').trim();
    const contact = raw.contactCd ?? raw.CONTACT_CD ?? raw.shareholder ?? '';
    const key =
      idx != null && String(idx).trim() !== ''
        ? `idx:${idx}`
        : `row:${rowCurrency}:${rowType}:${contact}`;
    if (seen.has(key)) continue;
    seen.add(key);

    sum += parseShareCapitalAmount(
      raw.issuedCapital ?? raw.issuedShareCapital ?? raw.ISSUED_SHARE_CAPITAL,
    );
  }
  return sum;
}

/** Entity Shares list — dedupe by shareCapitalIdx, sum issued for currency + class. */
export function sumEntityIssuedBaseline(
  shares: Array<Record<string, unknown>>,
  currencyCd: string,
  classOfSharesCd: string,
  classLabel?: string,
): number {
  const currency = String(currencyCd || '').trim();
  const shareType = classOfSharesToShareType(classOfSharesCd, classLabel);
  if (!currency || !shareType) return 0;

  const seen = new Set<string>();
  let sum = 0;
  for (const raw of shares) {
    const rowCurrency = String(raw.currencyCode ?? raw.CURRENCY_CD ?? '').trim();
    if (rowCurrency !== currency) continue;
    const rowType = String(raw.shareType ?? raw.share_type ?? '').trim();
    if (rowType !== shareType) continue;

    const idx = raw.shareCapitalIdx ?? raw.SHARE_CAPITAL_IDX;
    const contact = raw.contactCd ?? raw.CONTACT_CD ?? raw.shareholder ?? '';
    const key =
      idx != null && String(idx).trim() !== ''
        ? `idx:${idx}:${shareType}`
        : `row:${rowCurrency}:${rowType}:${contact}`;
    if (seen.has(key)) continue;
    seen.add(key);

    sum += parseShareCapitalAmount(
      raw.issuedCapital ?? raw.issuedShareCapital ?? raw.ISSUED_SHARE_CAPITAL,
    );
  }
  return sum;
}

export async function fetchEntitySharesList(
  entityCd: string,
): Promise<Array<Record<string, unknown>>> {
  const response: any = await entityApi.getEntityShares(entityCd);
  const data = response?.data?.data ?? response?.data ?? response ?? {};
  const shares = data?.shares;
  return Array.isArray(shares) ? (shares as Array<Record<string, unknown>>) : [];
}
